import {memo, useCallback, useState} from "react";
import {Box, FloatingPanel, HStack, Icon, IconButton, Stack, Text} from "@chakra-ui/react";
import {ChevronRightIcon, XMarkIcon} from "@heroicons/react/16/solid";
import {BeakerIcon} from "@heroicons/react/20/solid";
import {Portal} from "../../../components/Portal";
import {Button} from "../../../components/ui/button";
import {Scenario} from "./model";
import {DEMO_SEQUENCE, SCENARIOS} from "./scenarios";
import {useOeDemo} from "./gate";
import {resetScenario, setActiveScenario, useActiveScenarioId, useScenario} from "./store";
import {concludeAllTests, startAllTests} from "./testLifecycle";

const PANEL = {width: 320, height: 470};
/** Sit the panel just above its trigger in the bottom-left corner, rather than a fixed coordinate. */
const anchorToTrigger = () => ({x: 16, y: Math.max(72, window.innerHeight - PANEL.height - 76)});

/**
 * The scripted run, as a row of steps. Shows where you are in it and moves you on, so a demo does
 * not depend on finding the right row in a list of fourteen while someone is watching.
 */
const RunningOrder = memo(({onPick}: {onPick: (id: string) => void}) => {
  const activeId = useActiveScenarioId();
  const position = DEMO_SEQUENCE.indexOf(activeId);
  const next = DEMO_SEQUENCE[position + 1];

  return (
    <Stack gap={2} px={3} py={2.5} borderBottom="1px solid" borderColor="gray.700" bg="gray.950">
      <HStack justify="space-between">
        <Text fontSize="2xs" fontWeight="600" letterSpacing="wider" textTransform="uppercase" color="gray.400">
          Running order
        </Text>
        {position >= 0 && (
          <Text fontSize="2xs" color="gray.500" fontVariantNumeric="tabular-nums">
            {position + 1} of {DEMO_SEQUENCE.length}
          </Text>
        )}
      </HStack>
      <HStack gap={1}>
        {DEMO_SEQUENCE.map((id, i) => (
          <Box
            as="button"
            key={id}
            flex="1"
            h="6"
            rounded="sm"
            fontSize="2xs"
            fontWeight="600"
            color={id === activeId ? "white" : "gray.400"}
            bg={id === activeId ? "blue.600" : "whiteAlpha.100"}
            _hover={{bg: id === activeId ? "blue.600" : "whiteAlpha.300"}}
            onClick={() => onPick(id)}
            title={SCENARIOS.find(s => s.id === id)?.title}
          >
            {i + 1}
          </Box>
        ))}
      </HStack>
      <Button size="xs" variant="solid" colorPalette="blue" disabled={!next} onClick={() => next && onPick(next)}>
        <Text truncate>{next ? `Next — ${SCENARIOS.find(s => s.id === next)?.title}` : "End of run"}</Text>
        <Icon asChild boxSize="3.5" flex="none">
          <ChevronRightIcon />
        </Icon>
      </Button>
    </Stack>
  );
});

const ScenarioList = memo(({onPick}: {onPick: (id: string) => void}) => {
  const activeId = useActiveScenarioId();

  return (
    <Stack gap={1} p={2}>
      <Text fontSize="xs" color="gray.400" px={1} pb={1}>
        From the operating-effectiveness design document.
      </Text>
      {SCENARIOS.map(scenario => {
        const active = scenario.id === activeId;
        return (
          <Box
            as="button"
            key={scenario.id}
            textAlign="left"
            w="full"
            px={3}
            py={2}
            rounded="md"
            borderWidth="1px"
            borderColor={active ? "blue.400" : "transparent"}
            bg={active ? "blue.950" : "transparent"}
            _hover={{bg: active ? "blue.950" : "whiteAlpha.100"}}
            onClick={() => onPick(scenario.id)}
          >
            <Text fontSize="2xs" fontWeight="600" letterSpacing="wider" textTransform="uppercase" color="blue.300">
              {scenario.section}
            </Text>
            <Text fontSize="sm" fontWeight="600" color="white" lineHeight="1.3">
              {scenario.title}
            </Text>
            <Text fontSize="xs" color="gray.400" fontStyle="italic" lineHeight="1.3">
              {scenario.tagline}
            </Text>
          </Box>
        );
      })}
    </Stack>
  );
});

/** How far through its lifecycle the scenario's own tests are, for what the bar can offer. */
const testCounts = (scenario: Scenario) => {
  let planned = 0;
  let running = 0;
  for (const assessment of scenario.oe) {
    for (const item of assessment.basis) {
      if (item.status !== "planned" || item.evidence.kind !== "test" || !item.evidence.parameters) continue;
      if (assessment.underway?.[item.id]) running++;
      else planned++;
    }
  }
  return {planned, running};
};

const StepButton = ({children, disabled, onClick}: {children: string; disabled?: boolean; onClick: () => void}) => (
  <Button
    flex={1}
    size="xs"
    variant="ghost"
    fontWeight="500"
    color="white"
    bg="whiteAlpha.100"
    _hover={{bg: "whiteAlpha.200"}}
    _disabled={{color: "gray.600", bg: "transparent", cursor: "default"}}
    disabled={disabled}
    onClick={onClick}
  >
    {children}
  </Button>
);

/**
 * Walks the active scenario's tests through their lifecycle without hunting for the button on each
 * card. Acts on the resolved scenario, so a plan made in the wizard is stepped along with the rest.
 */
const LifecycleBar = memo(() => {
  const scenario = useScenario();
  const {planned, running} = testCounts(scenario);

  return (
    <Stack gap={1.5} px={2} py={2} borderTop="1px solid" borderColor="gray.700" flex="none">
      <Text fontSize="2xs" color="gray.400" px={1}>
        {planned === 0 && running === 0
          ? "No tests left to run in this scenario."
          : [planned > 0 && `${planned} not started`, running > 0 && `${running} running`].filter(Boolean).join(" · ")}
      </Text>
      <HStack gap={1}>
        <StepButton onClick={resetScenario}>Reset</StepButton>
        <StepButton disabled={planned === 0} onClick={() => startAllTests(scenario)}>
          Start tests
        </StepButton>
        <StepButton disabled={running === 0} onClick={() => concludeAllTests(scenario)}>
          Record results
        </StepButton>
      </HStack>
    </Stack>
  );
});

/**
 * Switches every demo surface between the design document's scenarios. Deliberately dark — it is a
 * demo control sitting over the product, not part of it.
 */
export const ScenarioPicker = memo(() => {
  const oeDemo = useOeDemo();
  const [open, setOpen] = useState(false);
  // Choosing a scenario dismisses the panel: it is captioned "Demo scenarios" and sits over a
  // customer-facing screen, so it should never be the thing left open after a switch.
  const pick = useCallback((id: string) => {
    setActiveScenario(id);
    setOpen(false);
  }, []);

  if (!oeDemo) return null;

  return (
    <FloatingPanel.Root
      open={open}
      onOpenChange={e => setOpen(e.open)}
      defaultSize={PANEL}
      defaultPosition={anchorToTrigger()}
      // Drag-only: a stray double-click on the header must not maximise the panel mid-demo.
      resizable={false}
    >
      <FloatingPanel.Trigger asChild>
        <IconButton
          position="fixed"
          bottom={4}
          left={4}
          size="sm"
          bg="gray.900"
          color="white"
          _hover={{bg: "gray.700"}}
          rounded="full"
          shadow="md"
          aria-label="Demo scenarios"
          zIndex="popover"
        >
          <Icon asChild>
            <BeakerIcon />
          </Icon>
        </IconButton>
      </FloatingPanel.Trigger>
      <Portal>
        <FloatingPanel.Positioner>
          <FloatingPanel.Content
            display="flex"
            flexDirection="column"
            overflow="hidden"
            p={0}
            gap={0}
            bg="gray.900"
            borderWidth="1px"
            borderColor="gray.700"
            shadow="dark-lg"
            rounded="lg"
          >
            <FloatingPanel.Header
              p={0}
              minH="auto"
              flex="none"
              display="flex"
              alignItems="center"
              bg="gray.900"
              borderBottom="1px solid"
              borderColor="gray.700"
            >
              <FloatingPanel.DragTrigger asChild>
                <Box flex="1" px={3} py={2} cursor="grab">
                  <FloatingPanel.Title fontSize="sm" fontWeight="600" color="white" whiteSpace="nowrap">
                    Demo scenarios
                  </FloatingPanel.Title>
                </Box>
              </FloatingPanel.DragTrigger>
              <FloatingPanel.CloseTrigger asChild>
                <IconButton
                  size="2xs"
                  variant="ghost"
                  aria-label="Close"
                  mr={2}
                  color="gray.400"
                  _hover={{bg: "whiteAlpha.200", color: "white"}}
                >
                  <Icon asChild>
                    <XMarkIcon />
                  </Icon>
                </IconButton>
              </FloatingPanel.CloseTrigger>
            </FloatingPanel.Header>
            <RunningOrder onPick={pick} />
            <FloatingPanel.Body flex="1" minH={0} overflowY="auto" p={0}>
              <ScenarioList onPick={pick} />
            </FloatingPanel.Body>
            <LifecycleBar />
          </FloatingPanel.Content>
        </FloatingPanel.Positioner>
      </Portal>
    </FloatingPanel.Root>
  );
});
